/**
 * Privacy-safe identity opportunity discovery for same-origin forms.
 *
 * Discovery reads control metadata only. Values are read only after a submit
 * event and only when consent plus the site-scoped form policy allow protected
 * identity delivery.
 */
import {
  inspectIdentityForm,
  readIdentityFormValue,
  type IdentityFormInspection,
} from "../lib/identity-fields";
import {
  isIdentityFormMarked as isMarked,
  resolveIdentityFormPolicyBlockReason,
  type IdentityFormSubmitter as IdentitySubmitterLike,
} from "../lib/identity-form-policy";
import { isActiveTestEnrollment } from "../lib/test-enrollment";
import type { PluginContext } from "../lib/types";
export { isSupportedIdentityForm } from "../lib/identity-form-policy";

import { registerPlugin } from "./register";

export type IdentityOpportunityBlockReason =
  | "consent_denied"
  | "sensitive_field"
  | "unsupported_source"
  | "transport_failure"
  | "workspace_policy";

type IdentityFormLike = HTMLFormElement & {
  elements: ArrayLike<unknown>;
};

const MAX_SCAN_WORK_PER_SLICE = 500;
const ATTACH_SHADOW_HOOK_STATE_KEY =
  "__prospixIdentityOpportunityAttachShadowHookV1";

type OpenShadowRootListener = (root: ShadowRoot) => void;

type AttachShadowHookState = {
  descriptor: PropertyDescriptor;
  listeners: Set<OpenShadowRootListener>;
  patched: Element["attachShadow"];
  prototype: Element;
};

type GlobalWithAttachShadowHook = typeof globalThis & {
  [ATTACH_SHADOW_HOOK_STATE_KEY]?: AttachShadowHookState;
};

function releaseOpenShadowRootListener(
  globalWithHook: GlobalWithAttachShadowHook,
  state: AttachShadowHookState,
  listener: OpenShadowRootListener,
) {
  state.listeners.delete(listener);
  if (state.listeners.size > 0) return;
  if (
    globalWithHook[ATTACH_SHADOW_HOOK_STATE_KEY] !== state ||
    state.prototype.attachShadow !== state.patched
  ) {
    return;
  }
  try {
    Object.defineProperty(state.prototype, "attachShadow", state.descriptor);
    delete globalWithHook[ATTACH_SHADOW_HOOK_STATE_KEY];
  } catch {
    // Leave an inert wrapper in place if another host locked the descriptor.
  }
}

function observeOpenShadowRoots(listener: OpenShadowRootListener) {
  if (typeof Element === "undefined") return () => {};
  const prototype = Element.prototype;
  const globalWithHook = globalThis as GlobalWithAttachShadowHook;
  const existing = globalWithHook[ATTACH_SHADOW_HOOK_STATE_KEY];
  if (existing && prototype.attachShadow === existing.patched) {
    existing.listeners.add(listener);
    return () =>
      releaseOpenShadowRootListener(globalWithHook, existing, listener);
  }

  const descriptor = Object.getOwnPropertyDescriptor(prototype, "attachShadow");
  if (!descriptor || typeof descriptor.value !== "function") return () => {};
  const original = descriptor.value as Element["attachShadow"];
  const listeners = new Set<OpenShadowRootListener>([listener]);
  const patched: Element["attachShadow"] = function (
    this: Element,
    init: ShadowRootInit,
  ) {
    const root = original.call(this, init);
    if (init.mode === "open") {
      for (const notify of Array.from(listeners)) {
        try {
          notify(root);
        } catch {
          // A discovery observer must never break the host's attachShadow call.
        }
      }
    }
    return root;
  };
  const state: AttachShadowHookState = {
    descriptor,
    listeners,
    patched,
    prototype,
  };
  try {
    Object.defineProperty(prototype, "attachShadow", {
      ...descriptor,
      value: patched,
    });
    globalWithHook[ATTACH_SHADOW_HOOK_STATE_KEY] = state;
  } catch {
    return () => {};
  }

  return () => releaseOpenShadowRootListener(globalWithHook, state, listener);
}

function formCaptureMode(ctx: PluginContext) {
  return ctx.config.identityCapture.discovery?.formMode ?? "automatic";
}

export function resolveIdentityOpportunityBlockReason(
  ctx: PluginContext,
  form: IdentityFormLike,
  submitter?: IdentitySubmitterLike | null,
): IdentityOpportunityBlockReason | undefined {
  const formPolicyReason = resolveIdentityFormPolicyBlockReason(
    ctx.config.identityCapture,
    form,
    submitter,
  );
  if (formPolicyReason) return formPolicyReason;
  if (
    ctx.consent.state !== "granted" ||
    ctx.consent.gpc ||
    ctx.consent.dnt ||
    (ctx.consent.cmp?.purposes?.marketing === false &&
      !isActiveTestEnrollment(ctx.consent))
  ) {
    return "consent_denied";
  }
  const readiness = ctx.identityCaptureReadiness?.();
  if (readiness && !readiness.ready) return readiness.reason;
  if (
    !ctx.canTrack(
      isActiveTestEnrollment(ctx.consent) ? "analytics" : "marketing",
    )
  )
    return "transport_failure";
  if (!ctx.canTrackCategory("identity_linkage")) return "workspace_policy";
  return undefined;
}

function reportProperties(
  ctx: PluginContext,
  form: IdentityFormLike,
  inspection: IdentityFormInspection,
  stage: "discovered" | "submitted",
  status: "detected" | "blocked" | "captured",
  blockedReason?: IdentityOpportunityBlockReason,
) {
  return {
    source: "form",
    stage,
    status,
    field_kinds: inspection.identityFields,
    field_count: inspection.fieldCount,
    sensitive_field_present: inspection.sensitiveFields.length > 0,
    form_mode: formCaptureMode(ctx),
    explicitly_marked: isMarked(form),
    ...(blockedReason ? { blocked_reason: blockedReason } : {}),
  };
}

export function createIdentityOpportunityPlugin(ctx: PluginContext) {
  let reported = new WeakMap<HTMLFormElement, string>();
  const roots = new Set<Document | ShadowRoot>();
  const observers = new Map<Document | ShadowRoot, MutationObserver>();
  type ScanWork =
    | { kind: "node"; node: Node }
    | { kind: "children"; nodes: ArrayLike<Node>; index: number };
  const scanWork: ScanWork[] = [];
  const pendingNodes = new Set<Node>();
  let scanWorkIndex = 0;
  let stopped = false;
  let scanQueued = false;
  let scanTimer: ReturnType<typeof setTimeout> | undefined;
  let releaseAttachShadowHook: (() => void) | undefined;

  const report = (
    form: IdentityFormLike,
    inspection: IdentityFormInspection,
    stage: "discovered" | "submitted",
    status: "detected" | "blocked" | "captured",
    blockedReason?: IdentityOpportunityBlockReason,
  ) => {
    const eventType =
      status === "blocked" && blockedReason === "consent_denied"
        ? "identity_opportunity_blocked_consent"
        : status === "captured"
          ? "identity_opportunity_captured"
          : status === "blocked"
            ? "identity_opportunity_blocked"
            : "identity_opportunity_detected";
    ctx.track(
      eventType,
      // Consent-blocked telemetry is deliberately a value-free counter. The
      // event name carries the reason without retaining form metadata.
      eventType === "identity_opportunity_blocked_consent"
        ? undefined
        : reportProperties(ctx, form, inspection, stage, status, blockedReason),
    );
  };

  const inspect = (form: IdentityFormLike, force = false) => {
    const inspection = inspectIdentityForm(form);
    const policyReason = resolveIdentityOpportunityBlockReason(ctx, form);
    const signature = JSON.stringify([
      inspection.identityFields,
      inspection.sensitiveFields,
      inspection.unsupportedIdentityHint,
      policyReason,
      formCaptureMode(ctx),
      isMarked(form),
    ]);
    if (!force && reported.get(form) === signature) return inspection;
    reported.set(form, signature);

    if (inspection.sensitiveFields.length > 0) {
      report(form, inspection, "discovered", "blocked", "sensitive_field");
    }
    if (inspection.identityFields.length === 0) {
      if (inspection.unsupportedIdentityHint) {
        report(form, inspection, "discovered", "blocked", "unsupported_source");
      }
      return inspection;
    }
    if (policyReason) {
      report(form, inspection, "discovered", "blocked", policyReason);
    } else {
      report(form, inspection, "discovered", "detected");
    }
    return inspection;
  };

  const removeRoot = (root: Document | ShadowRoot) => {
    observers.get(root)?.disconnect();
    observers.delete(root);
    root.removeEventListener("submit", onSubmit, true);
    roots.delete(root);
  };

  const pruneDisconnectedRoots = () => {
    for (const root of Array.from(roots)) {
      const host = (root as ShadowRoot).host;
      if (host && !host.isConnected) removeRoot(root);
    }
  };

  const enqueueNode = (node: Node | null | undefined) => {
    if (!node || pendingNodes.has(node)) return;
    pendingNodes.add(node);
    scanWork.push({ kind: "node", node });
  };

  const enqueueChildren = (nodes: ArrayLike<Node> | null | undefined) => {
    if (!nodes || nodes.length === 0) return;
    scanWork.push({ kind: "children", nodes, index: 0 });
  };

  const owningForm = (node: Node): IdentityFormLike | undefined => {
    const candidate = node as Node & {
      closest?: (selector: string) => Element | null;
      elements?: ArrayLike<unknown>;
      form?: HTMLFormElement | null;
      tagName?: string;
    };
    if (candidate.tagName?.toLowerCase() === "form" && candidate.elements) {
      return candidate as unknown as IdentityFormLike;
    }
    if (candidate.form?.elements) return candidate.form as IdentityFormLike;
    try {
      const closest = candidate.closest?.("form") as IdentityFormLike | null;
      return closest?.elements ? closest : undefined;
    } catch {
      return undefined;
    }
  };

  const queueScan = (deferToNextTask = false) => {
    if (scanQueued || stopped) return;
    scanQueued = true;
    if (deferToNextTask && typeof setTimeout === "function") {
      scanTimer = setTimeout(flushScanWork, 0);
      return;
    }
    queueMicrotask(flushScanWork);
  };

  const registerRoot = (root: Document | ShadowRoot) => {
    if (roots.has(root)) return;
    roots.add(root);
    root.addEventListener("submit", onSubmit, { capture: true });
    if (typeof MutationObserver !== "undefined") {
      const observer = new MutationObserver((records) => {
        for (const record of records) {
          enqueueChildren(record.addedNodes);
          const form = owningForm(record.target);
          if (form) enqueueNode(form);
        }
        queueScan();
      });
      observer.observe(root, { childList: true, subtree: true });
      observers.set(root, observer);
    }
  };

  const processNode = (node: Node) => {
    const candidate = node as Node & {
      childNodes?: ArrayLike<Node>;
      elements?: ArrayLike<unknown>;
      host?: Element;
      isConnected?: boolean;
      shadowRoot?: ShadowRoot | null;
      tagName?: string;
    };
    if (candidate.host && !candidate.host.isConnected) return;
    if (candidate.isConnected === false) return;
    if (candidate.nodeType === 9 || candidate.host) {
      registerRoot(candidate as unknown as Document | ShadowRoot);
    }
    if (candidate.tagName?.toLowerCase() === "form" && candidate.elements) {
      inspect(candidate as unknown as IdentityFormLike);
    }
    try {
      if (candidate.shadowRoot) {
        registerRoot(candidate.shadowRoot);
        enqueueNode(candidate.shadowRoot);
      }
    } catch {
      // Host elements can expose throwing accessors. Ignore that subtree.
    }
    try {
      enqueueChildren(candidate.childNodes);
    } catch {
      // A detached or host-defined node may expose a throwing child list.
    }
  };

  function flushScanWork() {
    scanQueued = false;
    scanTimer = undefined;
    if (stopped) return;
    pruneDisconnectedRoots();
    let processed = 0;
    while (
      scanWorkIndex < scanWork.length &&
      processed < MAX_SCAN_WORK_PER_SLICE
    ) {
      const work = scanWork[scanWorkIndex++];
      if (!work) continue;
      processed += 1;
      if (work.kind === "children") {
        const child = work.nodes[work.index];
        const nextIndex = work.index + 1;
        if (nextIndex < work.nodes.length) {
          scanWork.push({ ...work, index: nextIndex });
        }
        enqueueNode(child);
        continue;
      }
      pendingNodes.delete(work.node);
      processNode(work.node);
    }
    if (scanWorkIndex > 0) {
      scanWork.splice(0, scanWorkIndex);
      scanWorkIndex = 0;
    }
    if (scanWork.length > 0) queueScan(true);
  }

  const queueFullScan = () => {
    if (stopped || typeof document === "undefined") return;
    enqueueNode(document);
    for (const root of roots) enqueueNode(root);
    queueScan();
  };

  const onSubmit = (event: Event) => {
    const form = event.target as IdentityFormLike | null;
    if (!form?.elements) return;
    const inspection = inspect(form);
    if (inspection.identityFields.length === 0) return;

    const submitter = (event as SubmitEvent).submitter as
      | IdentitySubmitterLike
      | null
      | undefined;
    const blockedReason = resolveIdentityOpportunityBlockReason(
      ctx,
      form,
      submitter,
    );
    if (blockedReason) {
      report(form, inspection, "submitted", "blocked", blockedReason);
      return;
    }

    const email = readIdentityFormValue(form, "email");
    const phone = readIdentityFormValue(form, "phone");
    const externalId = readIdentityFormValue(form, "externalId");
    const profile = {
      fullName: readIdentityFormValue(form, "fullName"),
      company: readIdentityFormValue(form, "company"),
      jobTitle: readIdentityFormValue(form, "jobTitle"),
      website: readIdentityFormValue(form, "website"),
      first: readIdentityFormValue(form, "first"),
      middle: readIdentityFormValue(form, "middle"),
      last: readIdentityFormValue(form, "last"),
      street: readIdentityFormValue(form, "street"),
      city: readIdentityFormValue(form, "city"),
      state: readIdentityFormValue(form, "state"),
      postalCode: readIdentityFormValue(form, "postalCode"),
      country: readIdentityFormValue(form, "country"),
    };
    const hasProfile = Object.values(profile).some(Boolean);
    if (!email && !phone && !externalId && !hasProfile) {
      report(form, inspection, "submitted", "blocked", "unsupported_source");
      return;
    }

    void ctx
      .captureIdentity({
        source: isMarked(form) ? "marked_form" : "automatic_form",
        email,
        phone,
        externalId,
        ...(hasProfile ? { profile } : {}),
      })
      .then((captured) => {
        if (captured) {
          report(form, inspection, "submitted", "captured");
        } else {
          report(form, inspection, "submitted", "blocked", "transport_failure");
        }
      })
      .catch(() => {
        report(form, inspection, "submitted", "blocked", "transport_failure");
      });
  };

  const onConsentChanged = () => {
    reported = new WeakMap<HTMLFormElement, string>();
    queueFullScan();
  };

  return {
    onInit() {
      if (typeof document === "undefined") return;
      releaseAttachShadowHook = observeOpenShadowRoots((root) => {
        registerRoot(root);
        enqueueNode(root);
        queueScan();
      });
      document.addEventListener("DOMContentLoaded", queueFullScan, {
        once: true,
      });
      enqueueNode(document);
      flushScanWork();
      if (typeof window !== "undefined") {
        window.addEventListener("prospix:consent-changed", onConsentChanged);
      }
    },
    onUnload() {
      stopped = true;
      if (scanTimer !== undefined) clearTimeout(scanTimer);
      releaseAttachShadowHook?.();
      releaseAttachShadowHook = undefined;
      for (const root of Array.from(roots)) removeRoot(root);
      observers.clear();
      roots.clear();
      scanWork.length = 0;
      pendingNodes.clear();
      document.removeEventListener("DOMContentLoaded", queueFullScan);
      if (typeof window !== "undefined") {
        window.removeEventListener("prospix:consent-changed", onConsentChanged);
      }
    },
  };
}

registerPlugin("identityopportunity", createIdentityOpportunityPlugin);
